import type { ReactNode } from "react";
import { SteckbriefKopierknopf } from "./steckbrief-kopierknopf";

/** Ein Wert der Steckbrief-Leiste. Der Aufrufer liefert fertigen Text bzw. ein Abzeichen; `wert: null` = nicht gepflegt. */
export interface SteckbriefZelle {
  id: string;
  label: string;
  wert: ReactNode | null;
  /** Graue Zeile unter dem Wert (Ort darüber, Frist, Herkunft). */
  zusatz?: string;
  /** Text, den der Kopierknopf der Zelle in die Zwischenablage legt (nur mit Wert; Beschriftung „<Label> kopieren“). Fehlt = kein Knopf. */
  kopierText?: string;
}

// Raster (P-C 8, Betreiber 03.11.2026, Variante B): ab `md` ein 12er-Raster, jede Reihe verteilt sich gleichmäßig über die volle Breite.
// Mobil 2 Spalten (bei ungerader Zahl füllt die letzte Zelle beide). Ab `xl` stehen 5 bzw. 6 Zellen in EINER Reihe.
// Feste, vollständige Klassenketten je Zellzahl und Position (nie zusammengesetzt, R8).
const RASTER = {
  zwoelf: "grid grid-cols-2 md:grid-cols-12 gap-y-4 -ms-4",
  fuenfXl: "grid grid-cols-2 md:grid-cols-12 xl:grid-cols-5 gap-y-4 -ms-4",
  sechsXl: "grid grid-cols-2 md:grid-cols-12 xl:grid-cols-6 gap-y-4 -ms-4",
} as const;

function rasterFuer(n: number): string {
  if (n === 5) return RASTER.fuenfXl;
  if (n === 6) return RASTER.sechsXl;
  return RASTER.zwoelf;
}

const ZELLE = "min-w-0 ps-4 pe-2 border-s border-line-2";

/**
 * Klassen je Zelle (Position von links nach rechts, Reihe für Reihe), nach Zellzahl 1 bis 8.
 * 4 Spalten ab `md` bei 4, 7 und 8 Zellen (Reihe 1 = 4 Zellen; 7 = 4 + 3 mit Spanne 3/4), sonst 3 pro Reihe (5 = 3 + 2, 6 = 3 + 3).
 */
const ZELLEN: Record<number, readonly string[]> = {
  1: ["md:col-span-12"],
  2: ["md:col-span-6", "md:col-span-6"],
  3: ["md:col-span-4", "md:col-span-4", "col-span-2 md:col-span-4"],
  4: ["md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3"],
  5: ["md:col-span-4 xl:col-span-1", "md:col-span-4 xl:col-span-1", "md:col-span-4 xl:col-span-1", "md:col-span-6 xl:col-span-1", "col-span-2 md:col-span-6 xl:col-span-1"],
  6: ["md:col-span-4 xl:col-span-1", "md:col-span-4 xl:col-span-1", "md:col-span-4 xl:col-span-1", "md:col-span-4 xl:col-span-1", "md:col-span-4 xl:col-span-1", "md:col-span-4 xl:col-span-1"],
  7: ["md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-4", "md:col-span-4", "col-span-2 md:col-span-4"],
  8: ["md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3", "md:col-span-3"],
};

/** Mehr als 8 Zellen: 4 pro Reihe, ohne Ausgleich der letzten Reihe (nicht vorgesehen, siehe Design-Guide). */
const ZELLE_VIELE = "md:col-span-3";

function zellenKlassen(n: number, i: number): string {
  return `${ZELLE} ${ZELLEN[n]?.[i] ?? ZELLE_VIELE}`;
}

/**
 * Steckbrief-Leiste (Musterkatalog M-187, Design-Guide `steckbrief.md`): die wichtigsten Werte eines Objekts in EINER Karte, durch
 * senkrechte Linien getrennt (Katalog `divided-summary-kpi-card`). Rein darstellend: Welche Werte ein Typ trägt, entscheidet die
 * Quelle des Moduls, welche die Person lesen darf, der Lader (r110); der Baustein prüft nichts. Nicht für Kennzahlen mit Absprung
 * (dafür `KpiKarte`) und nicht für Eingaben.
 */
export function Steckbrief({ id, zellen, beschriftung = "Steckbrief" }: { id?: string; zellen: readonly SteckbriefZelle[]; beschriftung?: string }) {
  if (zellen.length === 0) return null;
  const n = zellen.length;
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-kpi-cards/divided-summary-kpi-card */}
      <section id={id} aria-label={beschriftung} className="p-5 bg-card border border-card-line shadow-2xs rounded-xl">
        <div className="overflow-hidden">
          <dl className={rasterFuer(n)}>
            {zellen.map((z, i) => (
              <div key={z.id} className={zellenKlassen(n, i)}>
                <dt className="text-sm text-muted-foreground-1">{z.label}</dt>
                <dd className="mt-1 text-base font-medium text-foreground break-words">
                  {z.wert === null ? (
                    <span className="text-sm font-normal text-muted-foreground-1">nicht gepflegt</span>
                  ) : z.kopierText ? (
                    <span className="inline-flex flex-wrap items-center gap-x-1">
                      {z.wert}
                      <SteckbriefKopierknopf label={z.label} text={z.kopierText} />
                    </span>
                  ) : (
                    z.wert
                  )}
                </dd>
                {z.zusatz && <dd className="text-sm text-muted-foreground-1 break-words">{z.zusatz}</dd>}
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  );
}
